import { t } from '@lingui/core/macro';
import type { UserGroupAddressRecord } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiBasicCard, NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { getLongAddressName } from 'noddi-web';
import { useState } from 'react';
import { AddressDialog } from './AddressDialog';
import { addressLabelIcons, getAddressLabel } from './addressLabels';

type Props = {
  userGroupAddress: UserGroupAddressRecord;
  userGroupId: number;
};

export const Address = ({ userGroupAddress, userGroupId }: Props) => {
  const { address } = userGroupAddress;
  const addressLabel = userGroupAddress.customLabel || getAddressLabel(userGroupAddress.label);
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const {
    mutate: removeAddress,
    isPending: isRemovePending,
    error: removeError
  } = noddiAsync.usePost({
    type: URLKeys.postDeleteUserGroupAddresses,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQuery({ urlKey: URLKeys.getUserGroupAddresses });
        setIsRemoving(false);
      }
    }
  });

  return (
    <>
      <NoddiBasicCard className='flex flex-col gap-4'>
        <div className='flex items-center gap-2'>
          <NoddiIcon name={addressLabelIcons[userGroupAddress.label]} size='medium' />
          <div>
            <p>{addressLabel}</p>
            <p className='text-muted-foreground'>{getLongAddressName(address)}</p>
          </div>
        </div>
        {userGroupAddress.instructions && (
          <p className='text-sm text-muted-foreground'>{userGroupAddress.instructions}</p>
        )}
        <div className='flex justify-end gap-2'>
          <NoddiButton size='small' variant='outline' onClick={() => setIsEditing(true)}>
            {t`Edit`}
          </NoddiButton>
          <NoddiButton size='small' variant='destructive' onClick={() => setIsRemoving(true)}>
            {t`Remove`}
          </NoddiButton>
        </div>
      </NoddiBasicCard>

      {isEditing && (
        <AddressDialog
          address={userGroupAddress}
          open={isEditing}
          onClose={() => setIsEditing(false)}
          userGroupId={userGroupId}
        />
      )}
      <NoddiDialog
        open={isRemoving}
        onClose={() => setIsRemoving(false)}
        title={t`Remove address`}
        description={t`Are you sure you want to remove this address?`}
        footer={
          <div className='flex flex-col gap-4'>
            {removeError && <ApiErrorMessageInternal error={removeError} />}
            <div className='flex justify-end gap-2'>
              <NoddiButton variant='outline' disabled={isRemovePending} onClick={() => setIsRemoving(false)}>
                {t`Cancel`}
              </NoddiButton>
              <NoddiButton
                variant='destructive'
                loading={isRemovePending}
                disabled={isRemovePending}
                onClick={() => removeAddress({ userGroupId, addressIds: [userGroupAddress.address.id] })}
              >
                {t`Remove address`}
              </NoddiButton>
            </div>
          </div>
        }
      />
    </>
  );
};
